import { Avatar, Typography, IconButton, Box } from "@mui/material";

import { EventMapView } from "./EventMapView";
import {
  AccessTime,
  EventAvailable,
  Clear,
  Check,
  LocationOnOutlined,
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
} from "@mui/icons-material";

import americanLogo from "../../../assets/images/american.png";
import visaLogo from "../../../assets/images/visa.png";
import mastercardLogo from "../../../assets/images/mastercard.png";
import paypalLogo from "../../../assets/images/paypal.png";
import { OrganizationInterface } from "../../../interfaces";

type ListItemWithIconProps = {
  icon: React.ReactNode;
  text: string;
};

const ListItemWithIcon = ({ icon, text }: ListItemWithIconProps) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      marginRight: 2,
      marginBottom: 1,
      padding: 0,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", margin: 0, padding: 0 }}>
      {icon}
    </Box>
    <Typography
      variant="body2"
      sx={{ margin: 0, paddingLeft: 0.5, fontSize: 16 }}
    >
      {text}
    </Typography>
  </Box>
);

const formatDate = (dateString: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    month: "long",
    day: "numeric",
  };
  const date = new Date(dateString);
  return date.toLocaleDateString("es-ES", options);
};

const formatTime = (dateTimeString: string): string => {
  const date = new Date(dateTimeString);
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  };
  return date.toLocaleTimeString("en-US", options);
};

interface EventDataViewProps {
  description: string;
  address: string;
  startDate: string;
  endDate: string;
  latitude: string;
  longitude: string;
  title: string;
  organization: OrganizationInterface;
  venueDetails: VenueDetail[];
}

interface VenueDetail {
  detail: string;
  indicator: boolean;
}

export const EventDataView = ({
  description,
  address,
  startDate,
  latitude,
  longitude,
  organization,
  title,
  venueDetails,
}: EventDataViewProps) => {
  const positiveDetails = venueDetails.filter((detail) => detail.indicator);
  const negativeDetails = venueDetails.filter((detail) => !detail.indicator);

  return (
    <Box
      sx={{
        backgroundColor: "transparent",
        boxShadow: "none",
        padding: 3,
        borderRadius: 2,
      }}
    >
      <Typography
        sx={{ mb: 2, fontSize: 64 }}
        variant="h4"
        color="text.primary"
        component="div"
      >
        {title}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          mb: 2,
          gap: 0,
        }}
      >
        <ListItemWithIcon
          icon={<EventAvailable sx={{ color: "white", mr: 0 }} />}
          text={formatDate(startDate)}
        />
        <ListItemWithIcon
          icon={<AccessTime sx={{ color: "white", ml: 0 }} />}
          text={formatTime(startDate)}
        />
      </Box>

      <Typography
        sx={{ mb: 2, fontSize: 14 }}
        variant="body1"
        color="white"
        component="div"
      >
        {description}
      </Typography>

      {venueDetails.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{ mb: 1, fontWeight: "bold", fontSize: 32 }}
            variant="h6"
            color="text.primary"
          >
            Detalles del evento
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              justifyContent: "flex-start",
              gap: 0,
            }}
          >
            {positiveDetails.map((detail, index) => (
              <ListItemWithIcon
                key={index}
                icon={<Check />}
                text={detail.detail}
              />
            ))}
            {negativeDetails.map((detail, index) => (
              <ListItemWithIcon
                key={index}
                icon={<Clear sx={{ color: "white" }} />}
                text={detail.detail}
              />
            ))}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          mt: 2,
        }}
      >
        <ListItemWithIcon
          icon={<LocationOnOutlined sx={{ color: "white", mr: 1 }} />}
          text={address}
        />
      </Box>

      {latitude && longitude && (
        <Box sx={{ mt: 2 }}>
          <EventMapView longitude={longitude} latitude={latitude} />
        </Box>
      )}
      <Box sx={{ mt: 4 }}>
        <Typography
          sx={{ mb: 1, fontWeight: "bold", fontSize: 32 }}
          variant="h6"
          color="text.primary"
        >
          Métodos de pago
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 3,
          }}
        >
          <img
            src={americanLogo}
            alt="American Express"
            style={{ height: 40, borderRadius: 10 }}
          />
          <img
            src={visaLogo}
            alt="Visa"
            style={{ height: 40, borderRadius: 10 }}
          />
          <img
            src={mastercardLogo}
            alt="MasterCard"
            style={{ height: 40, borderRadius: 10 }}
          />
          <img
            src={paypalLogo}
            alt="PayPal"
            style={{ height: 40, borderRadius: 10 }}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 4 }}>
        <Typography
          sx={{ mb: 1, fontWeight: "bold", fontSize: 32 }}
          variant="h6"
          color="text.primary"
        >
          Organiza
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Avatar
            src={organization.logo}
            alt={organization.name}
            sx={{ width: 56, height: 56 }}
          />
          <Box>
            <Typography variant="body1" color="white" fontSize={14}>
              {organization.name}
            </Typography>
            <Typography variant="body2" color="white" fontSize={14}>
              {organization.phoneNumber}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            mt: 2,
            gap: 3,
          }}
        >
          <IconButton
            component="a"
            href={organization.facebook}
            target="_blank"
          >
            <Facebook sx={{ color: "purple" }} />
          </IconButton>
          <IconButton component="a" href={organization.twitter} target="_blank">
            <Twitter sx={{ color: "purple" }} />
          </IconButton>
          <IconButton
            component="a"
            href={organization.instagram}
            target="_blank"
          >
            <Instagram sx={{ color: "purple" }} />
          </IconButton>
          <IconButton
            component="a"
            href={organization.linkedin}
            target="_blank"
          >
            <LinkedIn sx={{ color: "purple" }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
